<script setup lang="ts">
import type { BreadcrumbProps } from '@nuxt/ui';
import type { Ref } from 'vue';
import type { FoldableBreadcrumbItem, RenderableBreadcrumbItem } from '#layers/ui/types/breadcrumb-foldable';
import { computed, onMounted, shallowRef, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { omit } from '#layers/base/public';
import { BREADCRUMB_ELLIPSIS_SIZE_PX, BREADCRUMB_TRAILING_SPACE_PX, LAST_BREADCRUMB_ITEM_MIN_WIDTH_PX, useBreadcrumbOverflow } from '#layers/ui/composables/use-breadcrumb-overflow';
import { useEllipsisPopover } from '#layers/ui/composables/use-ellipsis-popover';
import { breadcrumbButtonFolded } from '#layers/ui/theme/components/_helpers';
import { BREADCRUMB_ELLIPSIS_PANEL_ATTR } from '#layers/ui/utils/is-inside-breadcrumb-ellipsis-panel';

interface Props {
	items: FoldableBreadcrumbItem[];
	/** Forwarded to `DBreadcrumb`, merged over the internal default. */
	ui?: BreadcrumbProps['ui'];
}

const { items, ui } = defineProps<Props>();

const { t } = useI18n();

const INTERNAL_SLOTS = ['separator', 'ellipsis'];
const rootStyle = {
	'--last-crumb-min-width': `${LAST_BREADCRUMB_ITEM_MIN_WIDTH_PX}px`,
	'--ellipsis-size': `${BREADCRUMB_ELLIPSIS_SIZE_PX}px`,
	'--trailing-space': `${BREADCRUMB_TRAILING_SPACE_PX}px`,
};

const { rootRef, listRef, ellipsisContentRef } = useMeasurementRefs();

const { renderItems, foldedItems, lastCrumbReleased } = useBreadcrumbOverflow({
	rootRef,
	listRef,
	items: () => items,
});

const { displayItems, mergedUi } = useDisplayItems(renderItems, lastCrumbReleased);
const { ellipsisContent } = useEllipsisPopover(ellipsisContentRef);
const foldedLabel = computed(() => t('breadcrumb.show_collapsed', foldedItems.value.length));

function isMeasuringRowSlot(slotProps: unknown): boolean {
	return Boolean((slotProps as { item?: RenderableBreadcrumbItem }).item?.folded);
}

function useMeasurementRefs() {
	const rootRef = useTemplateRef<HTMLElement>('root');
	const ellipsisContentRef = useTemplateRef<HTMLElement>('ellipsisPanel');

	// DBreadcrumb owns the <ul>, so capture it once on mount
	const listRef = shallowRef<HTMLElement | null>(null);

	onMounted(() => {
		listRef.value = rootRef.value?.querySelector('[data-slot="list"]') ?? null;
	});

	return { rootRef, listRef, ellipsisContentRef };
}

function useDisplayItems(
	renderItems: Readonly<Ref<RenderableBreadcrumbItem[]>>,
	lastCrumbReleased: Readonly<Ref<boolean>>,
) {
	const displayItems = computed(() => renderItems.value.map((item, index) => ({ ...item, ui: itemUi(item, index) })));
	const mergedUi = computed<BreadcrumbProps['ui']>(() => ({ link: 'text-sm', ...ui }));

	return { displayItems, mergedUi };

	function itemUi(item: RenderableBreadcrumbItem, index: number) {
		return {
			...item.ui,

			item: [
				item.ui?.item,
				item.switcher && 'px-0', // Switcher triggers bring their own padding
				crumbClass(item, index),
			].filter(Boolean).join(' '),
			separator: item.folded ? `${item.ui?.separator ?? ''} hidden`.trim() : item.ui?.separator,
		};
	}

	function crumbClass(item: RenderableBreadcrumbItem, index: number): string {
		// Visible crumbs are `shrink-0` so the row overflows and the engine folds instead of shrinking them.
		if (item.ellipsis) return 'shrink-0';

		// The last crumb releases to a min width once the middle has folded
		const isLast = index === renderItems.value.length - 1;
		if (isLast) return lastCrumbReleased.value ? 'min-w-(--last-crumb-min-width) shrink' : 'shrink-0';

		const cap = item.switcher ? 'shrink-0' : 'max-w-64 shrink-0';

		// Folded crumbs stay in the DOM (hidden, capped) for measurement
		return item.folded ? `${cap} absolute invisible` : cap;
	}
}
</script>

<template>
	<div
		ref="root"
		:style="rootStyle"
		class="flex min-w-0 flex-1 items-center overflow-hidden pe-(--trailing-space)"
	>
		<DBreadcrumb :items="displayItems" :ui="mergedUi">
			<template #separator>
				/
			</template>

			<template v-for="(_, name) in omit($slots, INTERNAL_SLOTS)" #[name]="slotProps">
				<slot :name v-bind="slotProps" :folded="false" :measuring="isMeasuringRowSlot(slotProps)" />
			</template>

			<template #ellipsis>
				<DPopover :content="ellipsisContent">
					<DTooltip :text="foldedLabel">
						<DButton
							color="neutral"
							variant="ghost"
							icon="lucide:ellipsis"
							:aria-label="foldedLabel"
							size="sm"
							class="size-(--ellipsis-size) rounded-sm"
						/>
					</DTooltip>

					<template #content>
						<div
							ref="ellipsisPanel"
							:[BREADCRUMB_ELLIPSIS_PANEL_ATTR]="true"
							class="flex max-w-80 min-w-55 flex-col gap-0.5 p-2"
						>
							<template v-for="(foldedItem, i) in foldedItems" :key="i">
								<div v-if="foldedItem.slot" class="contents">
									<slot :name="foldedItem.slot" :item="foldedItem" :folded="true" :measuring="false" />
								</div>

								<DButton
									v-else
									color="neutral"
									variant="ghost"
									:to="foldedItem.to"
									:icon="foldedItem.icon"
									:label="foldedItem.label"
									block
									:class="breadcrumbButtonFolded"
									:ui="{ leadingIcon: 'size-4 text-toned' }"
								/>
							</template>
						</div>
					</template>
				</DPopover>
			</template>
		</DBreadcrumb>
	</div>
</template>
